import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  isKnownPackage as isPackageKnown,
  isKnownPersona as isPersonaKnown,
  isSummarisedPayload,
  calcLiteChartData,
  calcMarketSimulatorPackages,
  calcPackages,
  calcRanks,
  getLiteChartMode,
  resolveCjView
} from '@aytm/app/stats/page/question-views/cjc/lib/model-data'
import {
  SUMMARY_CHART_VIEW,
  calcExportData,
  calcReportParamsSection,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/cjc/lib/report-params'
import {
  calcAllUncollapsed,
  calcToggledUncollapsed
} from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'
import { positionsToPackage } from '@aytm/app/stats/page/question-views/cjc/lib/calc'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/sh/lib/display-range'

// Owns the conjoint block state: the package the chart draws, which batches are hidden or
// expanded, the utility mode, the cluster persona, the summary-chart switch and the market
// simulator's list. Seeded from the question's report params and re-seeded whenever any of them
// changes outside the block.
//
// The page view never parses those params: it receives an api on mount and pulls the payloads it
// needs (the section to write, the export payload, the chart's axis) when a change is announced
// through onStateChange.
export default function useConjointState({
  answer,
  batches,
  questionType,
  badData,
  defaultSortOrder,
  defaultChartMode,
  questionChartMode,
  globalChartMode,
  globalZoomLevel,
  fullViewOnly,
  params,
  paramsRevision,
  onReady,
  onStateChange
}) {
  // A full-only mount offers no way back from the Attribute View, so neither a pinned one nor a pinned
  // lite slice (which draws only the summary) is honoured; an unpinned large design still opens lite,
  // which keeps its cost bounded. Dropped wherever the params are read, since the host writes into the
  // same object in place.
  const viewParamsOf = source =>
    fullViewOnly && (source?.cjt || 'lite' === source?.cjv)
      ? { ...source, cjt: null, ...('lite' === source.cjv ? { cjv: null } : {}) }
      : source

  // Seeding needs packages, packages need a mode, and the mode is seeded - so the first pass
  // resolves the params against no packages and the median fallback fills in below.
  const [state, setState] = useState(() =>
    resolveReportParams({
      params: viewParamsOf(params),
      answer,
      defaultSortOrder,
      mainPackages: [],
      isKnownPackage: value => isPackageKnown(answer, value),
      isKnownPersona: value => isPersonaKnown(answer, value)
    })
  )

  // Set once the researcher edits the simulator's list; until then it shows the best package.
  const marketSimulatorUserChanged = useRef(false)

  const cjView = resolveCjView(batches, state.cjView)
  const isLiteView = 'lite' === cjView
  const isSummaryChartView = SUMMARY_CHART_VIEW === state.cjtView
  const isSummaryView = isLiteView || isSummaryChartView
  // The full view is asked for but the page still holds the reduced slice (or is re-fetching):
  // the view has flipped, the data behind it has not.
  const isAwaitingFullSlice =
    !isLiteView && !badData && ('loading' === answer || (Boolean(answer) && isSummarisedPayload(answer, state.persona)))

  const { packageRankSharesArr, packageRankScoresArr } = useMemo(
    () => calcRanks({ answer, mode: state.mode, badData, isLiteView }),
    [answer, state.mode, badData, isLiteView]
  )

  const { currentPackage, mainPackages } = useMemo(
    () =>
      calcPackages({
        answer,
        mode: state.mode,
        persona: state.persona,
        currentPackage: state.currentPackage,
        badData,
        isLiteView,
        packageRankSharesArr,
        packageRankScoresArr
      }),
    [
      answer,
      state.mode,
      state.persona,
      state.currentPackage,
      badData,
      isLiteView,
      packageRankSharesArr,
      packageRankScoresArr
    ]
  )

  // only an answer that carries the design can say which stored products exist
  const isKnownMarketPackage = answer?.featureLengths?.length ? value => isPackageKnown(answer, value) : null

  const marketSimulatorPackages = calcMarketSimulatorPackages({
    packages: state.marketSimulatorPackages,
    userChanged: marketSimulatorUserChanged.current,
    mainPackages,
    isKnownPackage: isKnownMarketPackage
  })

  // The snapshot carries the stored simulator list, not the seeded one: an untouched list has
  // nothing to persist, and the seeded package is only what the panel shows.
  const snapshot = useRef()
  snapshot.current = {
    state: { ...state, currentPackage },
    context: {
      mainPackages,
      answer,
      questionType,
      defaultChartMode,
      questionChartMode,
      globalZoomLevel,
      isKnownMarketPackage
    },
    isAwaitingFullSlice
  }

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}, reason = 'state') =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = reason

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the lite chart opens on once the question's and the report-wide defaults are applied
    // -- what the exporters draw by, as opposed to the stored mode, which is null until one is picked
    const resolveExportChartMode = () =>
      getLiteChartMode({
        chartMode: snapshot.current.state.chartMode,
        questionChartMode: snapshot.current.context.questionChartMode,
        globalChartMode
      })

    // only the summary chart draws an axis worth pinning
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const { state, context } = snapshot.current
      if (SUMMARY_CHART_VIEW !== state.cjtView) return null

      const data = calcLiteChartData({ answer: context.answer, batches, persona: state.persona })
      if (!data) return null

      return calcDisplayRange({
        chartMode: getLiteChartMode({ chartMode, questionChartMode: context.questionChartMode, globalChartMode }),
        zoomLevel: zoomLevel || state.zoomLevel,
        globalChartMode,
        viewMode: 'percents',
        answer: data.synthesizedAnswer,
        ignoreAmount: true
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => calcReportParamsSection(snapshot.current.state, snapshot.current.context),
      // pptx / summary bridge: what the block is showing. `cjView` is the pinned data slice the
      // exporters used to read off the report params; the deck resolves the default from the batches.
      getExportData: () => {
        const { state } = snapshot.current
        const chartModeResolved = resolveExportChartMode()

        return {
          ...calcExportData(state),
          cjView: state.cjView,
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null)
        }
      },
      getDisplayRange: resolveDisplayRange,
      // pptx bridge: the view has moved to full while the page still holds the reduced slice
      isAwaitingFullSlice: () => snapshot.current.isAwaitingFullSlice,
      update: onModelChange
    }
  }, [batches, globalChartMode, onModelChange])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  //
  // Keyed on the revision the shell bumps per host render, not on the contents of the params bag.
  // A host-driven render is a fresh read of the live model; a re-render React starts for itself
  // carries the revision it already had. Keying on the contents missed an undo restoring a value
  // the block had already moved away from locally, because the block's own write is silent and the
  // string it was handed had never changed.
  useEffect(() => {
    const resolved = resolveReportParams({
      params: viewParamsOf(params) ?? {},
      answer,
      defaultSortOrder,
      mainPackages: snapshot.current.context.mainPackages,
      isKnownPackage: value => isPackageKnown(answer, value),
      isKnownPersona: value => isPersonaKnown(answer, value)
    })

    // the saved list decides again, as on opening the report: a reset to a report whose simulator was
    // never edited shows the best package, not the list the researcher had emptied before it
    marketSimulatorUserChanged.current = false
    pendingReason.current = 'seed'
    setState(current => ({ ...current, ...resolved }))
  }, [params, paramsRevision, answer, defaultSortOrder, fullViewOnly])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  // Editing the list makes the seeded package real, so it is written out with the edit.
  const seedPackages = useCallback(() => {
    const { state, context } = snapshot.current

    return calcMarketSimulatorPackages({
      packages: state.marketSimulatorPackages,
      userChanged: marketSimulatorUserChanged.current,
      mainPackages: context.mainPackages,
      isKnownPackage: context.isKnownMarketPackage
    })
  }, [])

  const onNAChange = useCallback(
    value => {
      const seeded = seedPackages()
      marketSimulatorUserChanged.current = true
      onModelChange({ marketSimulatorNA: value, marketSimulatorPackages: seeded })
    },
    [onModelChange, seedPackages]
  )

  const addMarketSimulatorPackage = useCallback(() => {
    const seeded = seedPackages()
    const { currentPackage } = snapshot.current.state

    marketSimulatorUserChanged.current = true
    onModelChange({
      marketSimulatorPackages: [...seeded, (currentPackage || '').split('.').map(value => parseInt(value, 10))]
    })
  }, [onModelChange, seedPackages])

  const deleteMarketSimulatorPackage = useCallback(
    index => {
      marketSimulatorUserChanged.current = true
      // the panel's rows are the shown list, which leaves out a dropped product
      onModelChange({ marketSimulatorPackages: seedPackages().filter((_, i) => i !== index) })
    },
    [onModelChange, seedPackages]
  )

  const setMarketSimulatorPackage = useCallback(
    index => {
      const { context } = snapshot.current
      const packages = seedPackages()
      const picked = packages.length ? packages[index] : context.mainPackages[0]

      onModelChange({ currentPackage: Array.from(picked || []).join('.') })
    },
    [onModelChange, seedPackages]
  )

  const handlers = useMemo(
    () => ({
      onPackageChange: value => onModelChange({ currentPackage: value }),
      onHiddenBatchesChange: value => onModelChange({ hiddenBatches: value }),
      onModeChange: value => onModelChange({ mode: value }),
      onSortChange: value => onModelChange({ sortOrder: value }),
      onChartModeChange: value => onModelChange({ chartMode: value }),
      onZoomChange: (_key, level) => onModelChange({ zoomLevel: level }),
      onSelectPersona: value => {
        if (isPersonaKnown(snapshot.current.context.answer, value)) onModelChange({ persona: value })
      },
      onToggleCollapse: (position, collapsed) =>
        onModelChange({ uncollapsed: calcToggledUncollapsed(snapshot.current.state.uncollapsed, position, collapsed) }),
      onToggleAll: collapse =>
        onModelChange({
          uncollapsed: calcAllUncollapsed(
            (batches || []).filter(batch => !batch.na).map(batch => batch.position),
            collapse
          )
        }),
      onBatchPackageChange: (bPosition, avPosition) =>
        onModelChange({
          currentPackage: positionsToPackage(snapshot.current.state.currentPackage, bPosition, avPosition).join('.')
        }),
      // The attribute view is the summary chart; leaving it either drops back to the rich chart
      // or, when the block is on the cheap slice, asks the page for the full data set.
      onShowBar: () => onModelChange({ cjtView: SUMMARY_CHART_VIEW }),
      onShowVisual: () => onModelChange(isLiteView ? { cjView: 'full' } : { cjtView: null }),
      onNAChange,
      addMarketSimulatorPackage,
      deleteMarketSimulatorPackage,
      setMarketSimulatorPackage
    }),
    [
      batches,
      isLiteView,
      onModelChange,
      onNAChange,
      addMarketSimulatorPackage,
      deleteMarketSimulatorPackage,
      setMarketSimulatorPackage
    ]
  )

  return {
    ...state,
    currentPackage,
    mainPackages,
    marketSimulatorPackages,
    // the list as stored: the export falls back to the question's own top packages when the
    // researcher never built one, so the seeded package must not stand in for it
    storedMarketSimulatorPackages: state.marketSimulatorPackages,
    packageRankSharesArr,
    packageRankScoresArr,
    isLiteView,
    isSummaryChartView,
    isSummaryView,
    isAwaitingFullSlice,
    onModelChange,
    ...handlers
  }
}
